import React from 'react'
import { Separator } from '@/components/ui/separator'

// Scheme allowlist for markdown-authored URLs. The renderer is shared by the
// (trusted) marketing content AND tenant-authored blog posts (SEO hosting), so
// reject `javascript:` / `data:` / other dangerous schemes; allow only
// http(s)/mailto and relative/anchor URLs. Returns null when disallowed.
export function safeUrl(url: string): string | null {
  const trimmed = url.trim()
  if (/^(\/|#|\.\/|\.\.\/)/.test(trimmed)) return trimmed // relative / anchor
  if (/^https?:\/\//i.test(trimmed)) return trimmed
  if (/^mailto:/i.test(trimmed)) return trimmed
  return null
}

// Splits on `code`, **bold**, *italic*, or [link](url), then recurses into each
// part so nested inline like **[name](url)** renders as a clickable bold link.
// Order matters: code (literal, no recursion) → bold → italic → link.
export function renderInlineMarkdown(text: string): React.ReactNode {
  const parts = text.split(
    /(`[^`]+`|\*\*[^*]+\*\*|\*[^*\n]+\*|\[[^\]]+\]\([^)]+\))/g,
  )
  return parts.map((part, i) => {
    if (part.length > 1 && part.startsWith('`') && part.endsWith('`')) {
      return (
        <code
          key={i}
          className="bg-muted rounded px-1.5 py-0.5 font-mono text-[0.85em]"
        >
          {part.slice(1, -1)}
        </code>
      )
    }
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={i} className="text-foreground font-semibold">
          {renderInlineMarkdown(part.slice(2, -2))}
        </strong>
      )
    }
    if (part.length > 2 && part.startsWith('*') && part.endsWith('*')) {
      return <em key={i}>{renderInlineMarkdown(part.slice(1, -1))}</em>
    }
    const linkMatch = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/)
    if (linkMatch) {
      const href = safeUrl(linkMatch[2])
      // Disallowed scheme (e.g. javascript:) → render the link text only.
      if (href === null) {
        return (
          <React.Fragment key={i}>
            {renderInlineMarkdown(linkMatch[1])}
          </React.Fragment>
        )
      }
      const isExternal = /^https?:\/\//i.test(href)
      return (
        <a
          key={i}
          href={href}
          className="text-primary hover:text-primary/80 underline underline-offset-4"
          target={isExternal ? '_blank' : undefined}
          rel={isExternal ? 'noopener noreferrer' : undefined}
        >
          {renderInlineMarkdown(linkMatch[1])}
        </a>
      )
    }
    return part
  })
}

function parseTableRow(line: string): string[] {
  return line
    .split('|')
    .slice(1, -1)
    .map(cell => cell.trim())
}

function isTableSeparator(line: string): boolean {
  return /^\|[\s-:|]+\|$/.test(line.trim())
}

export function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')
}

export interface TocHeading {
  id: string
  text: string
  level: 2 | 3
}

// ATX heading: 1–6 leading `#`, a required space, optional trailing `#` closing
// sequence (CommonMark). Shared by the renderer AND the TOC extractor so a
// heading's slug id always matches its in-page anchor. Returns null for
// non-headings (incl. `#tag` with no space, and `#######` with 7+ hashes).
function parseHeading(line: string): { level: number; text: string } | null {
  const m = line.trim().match(/^(#{1,6})\s+(.+?)\s*#*$/)
  if (!m) return null
  return { level: m[1].length, text: m[2] }
}

export function extractHeadings(content: string): TocHeading[] {
  const headings: TocHeading[] = []
  let inFence = false
  for (const line of content.split('\n')) {
    // A `#` inside a fenced code block is code, not a heading — skip fences so
    // the TOC stays in sync with what the renderer actually emits as headings.
    if (line.trim().startsWith('```')) {
      inFence = !inFence
      continue
    }
    if (inFence) continue
    const heading = parseHeading(line)
    if (!heading) continue
    // Only H1–H3 carry the TOC (H1 is a demoted body heading — see
    // renderHeading); deeper levels stay out of the contents nav.
    if (heading.level <= 2) {
      headings.push({ id: slugify(heading.text), text: heading.text, level: 2 })
    } else if (heading.level === 3) {
      headings.push({ id: slugify(heading.text), text: heading.text, level: 3 })
    }
  }
  return headings
}

function renderHeading(
  { level, text }: { level: number; text: string },
  key: number,
): React.ReactNode {
  const id = slugify(text)
  const content = renderInlineMarkdown(text)
  // The page chrome renders the post title as the sole <h1>, so a body H1 (`#`)
  // is demoted to <h2> styling to keep a single canonical <h1> (a duplicate H1
  // is an on-page-SEO smell). Levels 2–6 map straight to <h2>–<h6>.
  switch (level) {
    case 1:
      return (
        <h2
          key={key}
          id={id}
          className="mt-12 mb-4 text-3xl font-bold tracking-tight"
        >
          {content}
        </h2>
      )
    case 2:
      return (
        <h2
          key={key}
          id={id}
          className="mt-10 mb-4 text-2xl font-semibold tracking-tight"
        >
          {content}
        </h2>
      )
    case 3:
      return (
        <h3
          key={key}
          id={id}
          className="mt-8 mb-3 text-xl font-semibold tracking-tight"
        >
          {content}
        </h3>
      )
    case 4:
      return (
        <h4 key={key} id={id} className="mt-6 mb-2 text-lg font-semibold">
          {content}
        </h4>
      )
    case 5:
      return (
        <h5 key={key} id={id} className="mt-6 mb-2 text-base font-semibold">
          {content}
        </h5>
      )
    default:
      return (
        <h6
          key={key}
          id={id}
          className="text-muted-foreground mt-6 mb-2 text-sm font-semibold tracking-wide uppercase"
        >
          {content}
        </h6>
      )
  }
}

export function renderMarkdownContent(content: string) {
  const lines = content.split('\n')
  const elements: React.ReactNode[] = []
  let i = 0

  while (i < lines.length) {
    const trimmed = lines[i].trim()

    // Fenced code block: ```lang ... ``` — content is literal (no inline parsing).
    if (trimmed.startsWith('```')) {
      const codeLines: string[] = []
      i++
      while (i < lines.length && lines[i].trim() !== '```') {
        codeLines.push(lines[i])
        i++
      }
      i++ // skip the closing fence
      elements.push(
        <pre
          key={`code-${i}`}
          className="border-border bg-muted my-6 overflow-x-auto rounded-lg border p-4 text-sm"
        >
          <code className="text-foreground font-mono">
            {codeLines.join('\n')}
          </code>
        </pre>,
      )
      continue
    }

    if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
      const tableLines: string[] = []
      while (i < lines.length) {
        const tl = lines[i].trim()
        if (tl.startsWith('|') && tl.endsWith('|')) {
          tableLines.push(tl)
          i++
        } else {
          break
        }
      }
      if (tableLines.length >= 2) {
        const headers = parseTableRow(tableLines[0])
        const bodyStart = isTableSeparator(tableLines[1]) ? 2 : 1
        const bodyRows = tableLines.slice(bodyStart).map(parseTableRow)

        elements.push(
          <div key={`table-${i}`} className="my-8 overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-border border-b">
                  {headers.map((h, hi) => (
                    <th
                      key={hi}
                      className="text-foreground px-4 py-3 text-left font-semibold"
                    >
                      {renderInlineMarkdown(h)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {bodyRows.map((row, ri) => (
                  <tr key={ri} className="border-border border-b last:border-0">
                    {row.map((cell, ci) => (
                      <td key={ci} className="text-muted-foreground px-4 py-3">
                        {renderInlineMarkdown(cell)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>,
        )
      }
      continue
    }

    // Blockquote: one or more consecutive `>` lines. A blank `>` line splits
    // the quote into separate paragraphs.
    if (trimmed.startsWith('>')) {
      const quoteLines: string[] = []
      while (i < lines.length && lines[i].trim().startsWith('>')) {
        quoteLines.push(lines[i].trim().replace(/^>\s?/, ''))
        i++
      }
      const paragraphs: string[] = []
      let buffer: string[] = []
      for (const ql of quoteLines) {
        if (ql.length === 0) {
          if (buffer.length) {
            paragraphs.push(buffer.join(' '))
            buffer = []
          }
        } else {
          buffer.push(ql)
        }
      }
      if (buffer.length) paragraphs.push(buffer.join(' '))
      elements.push(
        <blockquote
          key={`quote-${i}`}
          className="text-muted-foreground my-6 border-l-2 border-neutral-300 pl-4 italic dark:border-neutral-700"
        >
          {paragraphs.map((para, pi) => (
            <p key={pi} className="mb-2 leading-relaxed last:mb-0">
              {renderInlineMarkdown(para)}
            </p>
          ))}
        </blockquote>,
      )
      continue
    }

    const heading = parseHeading(trimmed)
    if (heading) {
      elements.push(renderHeading(heading, i))
    } else {
      const imgMatch = trimmed.match(/^!\[([^\]]*)\]\(([^)]+)\)$/)
      const imgSrc = imgMatch ? safeUrl(imgMatch[2]) : null
      if (imgMatch && imgSrc) {
        elements.push(
          <figure key={i} className="my-8">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={imgSrc} alt={imgMatch[1]} className="h-auto w-full" />
            {imgMatch[1] && (
              <figcaption className="text-muted-foreground mt-2 text-center text-sm">
                {imgMatch[1]}
              </figcaption>
            )}
          </figure>,
        )
      } else if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
        // Collect consecutive unordered list items
        const items: { text: string; idx: number }[] = []
        while (i < lines.length) {
          const t = lines[i].trim()
          if (t.startsWith('- ') || t.startsWith('* ')) {
            items.push({ text: t.slice(2), idx: i })
            i++
          } else if (
            t === '' &&
            i + 1 < lines.length &&
            (lines[i + 1].trim().startsWith('- ') ||
              lines[i + 1].trim().startsWith('* '))
          ) {
            i++
          } else {
            break
          }
        }
        elements.push(
          <ul
            key={`ul-${items[0].idx}`}
            className="mb-4 ml-6 list-disc space-y-1"
          >
            {items.map(item => (
              <li key={item.idx} className="text-muted-foreground">
                {renderInlineMarkdown(item.text)}
              </li>
            ))}
          </ul>,
        )
        continue
      } else if (/^\d+\.\s/.test(trimmed)) {
        // Collect ordered list items (allow blank lines between items)
        const items: { text: string; idx: number }[] = []
        while (i < lines.length) {
          const t = lines[i].trim()
          if (/^\d+\.\s/.test(t)) {
            items.push({ text: t.replace(/^\d+\.\s+/, ''), idx: i })
            i++
          } else if (
            t === '' &&
            i + 1 < lines.length &&
            /^\d+\.\s/.test(lines[i + 1].trim())
          ) {
            i++
          } else {
            break
          }
        }
        elements.push(
          <ol
            key={`ol-${items[0].idx}`}
            className="mb-4 ml-6 list-decimal space-y-1"
          >
            {items.map(item => (
              <li key={item.idx} className="text-muted-foreground">
                {renderInlineMarkdown(item.text)}
              </li>
            ))}
          </ol>,
        )
        continue
      } else if (/^---+$/.test(trimmed)) {
        elements.push(<Separator key={i} className="my-8" />)
      } else if (trimmed !== '') {
        elements.push(
          <p key={i} className="text-muted-foreground mb-4 leading-relaxed">
            {renderInlineMarkdown(trimmed)}
          </p>,
        )
      }
    }
    i++
  }

  return elements
}
